import { useState, Fragment, useMemo, memo } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Trans, useTranslation } from 'react-i18next';
import { convertConflictToSections, getNameWithCode } from '../services';
import { AutoSelectedAssignmentsTable } from './AutoSelectedConfigurations/AutoSelectedAssignmentsTable';
import type {
  ICollapsibleConflictTableType,
  IConflictTableProps,
  IConflictInnerTableType,
  IFeatureOrFamily,
  ICollapsibleConflictRowType,
  ISectionAssignments,
  IConflictDataSectionType,
  IConflictDataPresentationType,
  IConflictDialogConflictType,
} from '../../types';

/**
 * Renders a table displaying conflict data in the same layout as AutoSelectedAssignmentsTable.
 * Shows a product header, followed by a 3-column table (Feature, Option, Description) with section subheaders.
 * @param {IConflictInnerTableType} param0 The properties required for constructing and displaying the Table
 * @returns {JSX.Element} The Table displaying Feature, Option and Description columns for conflict values
 */
const CollapsibleInnerTable = memo(({ model, showCode }: Readonly<IConflictInnerTableType>) => {
  const { t } = useTranslation();

  if (!model) {
    return null;
  }

  const productKey = `${model.id} (${model.name})`;
  const sections = convertConflictToSections(model);

  return (
    <div className="productSection pt-10">
      <div className="productHeader">{productKey}</div>
      {model.isModelConflict && (
        <TableRow className="rowText">
          <TableCell
            className="tabCell subRowText text-left vertical-middle justify-left align-center"
            align="left"
          >
            <Box className="modelConflict-icon">
              <WarningIcon fontSize="small" />
            </Box>
            {t('conflictDialog.conflictTable.modelConflictMessage')}
            <b>
              {getNameWithCode(
                {
                  name: model.name,
                  id: model.id,
                  shortSalesText: model.shortSalesText,
                } as IFeatureOrFamily,
                showCode
              )}
            </b>
          </TableCell>
        </TableRow>
      )}
      <TableContainer className="productTableContainer">
        <Table aria-label={`assignments table for ${productKey}`}>
          <TableHead>
            <TableRow>
              <TableCell
                className="tabCell tabHead feature-column feature-header"
                variant="head"
                align="left"
              >
                {t('labels.feature')}
              </TableCell>
              <TableCell
                className="tabCell tabHead option-column feature-header"
                variant="head"
                align="left"
              >
                {t('labels.option')}
              </TableCell>
              <TableCell
                className="tabCell tabHead description-column description-header"
                variant="head"
                align="left"
              >
                {t('labels.description')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(sections).map(([sectionName, items]) => (
              <Fragment key={sectionName}>
                <TableRow className="sectionSubheader">
                  <TableCell
                    colSpan={3}
                    style={{ fontWeight: 'bold' }}
                    className="tabCell sectionSubheaderCell"
                  >
                    {sectionName}
                  </TableCell>
                </TableRow>
                {items.map((item) => (
                  <TableRow
                    key={`${productKey}_${sectionName}_${item.featureId}_${item.optionId}`}
                    className="tableRow"
                    hover
                  >
                    <TableCell className="tabCell feature-column" align="left">
                      {item.featureId}
                    </TableCell>
                    <TableCell className="tabCell option-column" align="left">
                      {item.optionId}
                    </TableCell>
                    <TableCell className="tabCell description-column" align="left">
                      {item.name}
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
});

CollapsibleInnerTable.displayName = 'CollapsibleInnerTable';

/**
 * Creates Row for a model as required for the collapsible table component
 * @param {ICollapsibleConflictRowType} props various properties that are required for generation of the row
 * @returns {JSX.Element} a row represnting a collapsible table for the given model based on the properties
 */
const CollapsibleTableRow = memo(
  ({ rowKey, model, expand, showCode }: Readonly<ICollapsibleConflictRowType>) => {
    if (!model) {
      return null;
    }

    return (
      <TableRow key={`${rowKey}_collapsible`} className="collapsibleTableRow">
        <TableCell className="pb-0 pt-0 bottomUnset" colSpan={2}>
          <Collapse in={expand} timeout="auto" unmountOnExit>
            <Box className="mt-3">
              <CollapsibleInnerTable model={model} showCode={showCode} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  }
);

CollapsibleTableRow.displayName = 'CollapsibleTableRow';

/**
 * Renders the table header with expand/collapse functionality for accordion sections.
 * Displays a header text and an icon button to toggle between expanded and collapsed states.
 * @param {Object} props - The component props
 * @param {boolean} props.accordionOpen - Current state of the accordion (true if expanded, false if collapsed)
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setAccordionOpen - State setter function to toggle accordion state
 * @param {string} props.header - The header text to display in the table header
 * @returns {JSX.Element} The table header component with expand/collapse button
 */
const RenderTableHeader = memo(
  ({
    accordionOpen,
    setAccordionOpen,
    header,
    showWarning = false,
  }: Readonly<{
    accordionOpen: boolean;
    setAccordionOpen: React.Dispatch<React.SetStateAction<boolean>>;
    header: string;
    showWarning?: boolean;
  }>) => {
    const { t } = useTranslation();
    return (
      <TableHead className="m-0 p-0">
        <TableRow>
          <TableCell className="tabCell subTableHeader w-90" variant="head" align="left">
            {header}
          </TableCell>
          <TableCell className="tabCell w-10" variant="head" align="right">
            {showWarning && (
              <Tooltip title={t('dialogBox.warning')}>
                <Box className="warning d-inline-flex align-center vertical-middle justify-center warning-icon-box">
                  <WarningIcon fontSize="small" />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={accordionOpen ? t('tooltip.collapseAll') : t('tooltip.expandAll')}>
              <IconButton
                aria-label={`${t('tooltip.collapseAll')}/${t('tooltip.expandAll')}`}
                size="medium"
                onClick={() => setAccordionOpen(!accordionOpen)}
                className="vertical-middle text-right"
                data-testid="collapse-button"
              >
                {accordionOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      </TableHead>
    );
  }
);

RenderTableHeader.displayName = 'RenderTableHeader';

/**
 * Creates a collapsible row for assignment differences
 */
const AssignmentCollapsibleRow = memo(
  ({
    assignedByRuleDifferences,
    expand,
  }: Readonly<{
    assignedByRuleDifferences: { differences: Record<string, ISectionAssignments>; count: number };
    expand: boolean;
  }>) => {
    return (
      <TableRow className="collapsibleTableRow">
        <TableCell className="pb-0 pt-0 bottomUnset" colSpan={2}>
          <Collapse in={expand} timeout="auto" unmountOnExit>
            <Box className="mt-3">
              <div className="auto-included-message">
                <Trans
                  i18nKey="newAssignment.autoIncludedMessage"
                  values={{ count: assignedByRuleDifferences.count }}
                  components={{ 1: <strong /> }}
                />
              </div>
              {Object.entries(assignedByRuleDifferences.differences).map(
                ([productKey, sections]) => (
                  <div key={productKey} className="assignment-section">
                    <AutoSelectedAssignmentsTable productKey={productKey} sections={sections} />
                  </div>
                )
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  }
);

AssignmentCollapsibleRow.displayName = 'AssignmentCollapsibleRow';

/**
 * Creates Collapsible table as required and defined by the properties
 * @param {ICollapsibleConflictTableType} param0 various properties that are required for generation of table
 * @returns {JSX.Element} a Collapsible Table representing all the conflicts that occur
 */
const CollapsibleTable = memo(
  ({ models, showCode, assignedByRuleDifferences }: Readonly<ICollapsibleConflictTableType>) => {
    const { t } = useTranslation();
    const hasAutoSelectedAssignments =
      assignedByRuleDifferences && assignedByRuleDifferences.count > 0;
    const [conflictsOpen, setConflictsOpen] = useState<boolean>(!hasAutoSelectedAssignments);
    const [assignmentsOpen, setAssignmentsOpen] = useState<boolean>(true);

    const conflictsCount = useMemo(() => {
      if (!models) return 0;

      const countConflictValues = (section: IConflictDataSectionType) =>
        section.conflicts.reduce(
          (conflictTotal: number, conflict: IConflictDialogConflictType) =>
            conflictTotal + conflict.values.length,
          0
        );

      const countSectionConflicts = (model: IConflictDataPresentationType) =>
        model.sections.reduce(
          (sectionTotal: number, section: IConflictDataSectionType) =>
            sectionTotal + countConflictValues(section),
          0
        );

      return models.reduce(
        (total: number, model: IConflictDataPresentationType) =>
          total + countSectionConflicts(model),
        0
      );
    }, [models]);

    if (!models) {
      return (
        <Box className="d-flex justify-center align-center h-100">
          {t('conflictDialog.conflictTable.noConflicts')}
        </Box>
      );
    }

    return (
      <TableContainer className="collapsibleTable-root overflow-auto">
        <Table stickyHeader aria-label="collapsible table">
          <RenderTableHeader
            accordionOpen={conflictsOpen}
            setAccordionOpen={setConflictsOpen}
            header={`${t('conflictDialog.conflictTable.conflictsWith')} (${conflictsCount})`}
            showWarning={models.some((model) => model.isModelConflict)}
          />

          <TableBody>
            {models.map((model) => (
              <CollapsibleTableRow
                key={model.id}
                rowKey={model.id}
                model={model}
                showCode={showCode}
                expand={conflictsOpen}
              />
            ))}
          </TableBody>
        </Table>
        {hasAutoSelectedAssignments && (
          <Table stickyHeader aria-label="collapsible table">
            <RenderTableHeader
              accordionOpen={assignmentsOpen}
              setAccordionOpen={setAssignmentsOpen}
              header={`${t('newAssignment.title')} (${assignedByRuleDifferences.count})`}
            />

            <TableBody>
              <AssignmentCollapsibleRow
                assignedByRuleDifferences={assignedByRuleDifferences}
                expand={assignmentsOpen}
              />
            </TableBody>
          </Table>
        )}
      </TableContainer>
    );
  }
);

CollapsibleTable.displayName = 'CollapsibleTable';

/**
 * A container to render the Conflicts
 * @param {IConflictTableProps} param0 properties for the ConflictTable component
 * @returns {JSX.Element} The component displaying the Conflicts
 */
export const ConflictTable = memo(
  ({ modelRows, showCode, assignedByRuleDifferences }: IConflictTableProps) => {
    return (
      <CollapsibleTable
        models={modelRows}
        showCode={showCode}
        assignedByRuleDifferences={assignedByRuleDifferences}
      />
    );
  }
);

ConflictTable.displayName = 'ConflictTable';
